import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { CalendarContainer, CalendarPagePending } from '~/components/calendar/calendar-container.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import type { Events } from '~/database/supabase/calendar/data.server.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import { getCalendarPageDataQueryOptions } from '~/server/calendar/calendar-page-data-query.ts';
import CalendarEventsContainer from '~/views/calendar/components/calendar-events-container.tsx';
import { WIDTH_FOR_3_MONTHS } from '~/views/calendar/utils/calendar.ts';

export const Route = createFileRoute('/calendar')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: CalendarPage,
	loader: ({ context }) => context.queryClient.ensureQueryData(getCalendarPageDataQueryOptions()),
	pendingComponent: CalendarPagePending,
	pendingMinMs: 250,
});

function CalendarPage() {
	const { data } = useSuspenseQuery(getCalendarPageDataQueryOptions());
	const { events, numberOfMonths: numberOfMonthsToDisplay, userLocale } = data;
	const [numberOfMonths, setNumberOfMonths] = useState(numberOfMonthsToDisplay);
	const [selectedDay, setSelectedDay] = useState<Date>(new Date(new Date().setHours(0, 0, 0, 0)));
	const [calendarEvents, setCalendarEvents] = useState<Events | undefined>(events);

	useEffect(() => {
		function handleResize() {
			if (window.innerWidth >= WIDTH_FOR_3_MONTHS) {
				setNumberOfMonths(3);
			} else {
				setNumberOfMonths(1);
			}
		}

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return (
		<ScrollArea
			classNames={{
				root: 'h-dvh',
				viewport: 'h-dvh',
			}}
		>
			<CalendarContainer
				calendarEvents={calendarEvents}
				numberOfMonths={numberOfMonths}
				selectedDay={selectedDay}
				setCalendarEvents={setCalendarEvents}
				setSelectedDay={setSelectedDay}
			/>
			<CalendarEventsContainer calendarEvents={calendarEvents} selectedDay={selectedDay} userLocale={userLocale} />
		</ScrollArea>
	);
}
